// ada-a11y-patches.js
// Theme-side runtime patches for accessibility issues introduced by
// third-party scripts that we can't fix at the source.
//
// Each patch is idempotent and uses a MutationObserver so it works on
// elements that get injected after first paint.
//
// 1. EDT translator app — the geolocator-detected country flag <img>
//    inside .edt is decorative and ships without an alt attribute,
//    triggering axe image-alt (WCAG 1.1.1 critical). We mark it
//    aria-hidden + alt="" so screen readers ignore it.

(function () {
  'use strict';

  function isEdtImg(el) {
    return el && el.nodeType === 1 && el.tagName === 'IMG'
      && el.parentElement && el.parentElement.classList
      && el.parentElement.classList.contains('edt');
  }

  function patchEdtImg(img) {
    if (!img || img.dataset.adaPatched) return;
    img.setAttribute('alt', '');
    img.setAttribute('aria-hidden', 'true');
    img.dataset.adaPatched = '1';
  }

  function patchAllExistingEdt(root) {
    (root || document).querySelectorAll('.edt > img').forEach(patchEdtImg);
  }

  patchAllExistingEdt();

  var observer = new MutationObserver(function (mutations) {
    for (var i = 0; i < mutations.length; i++) {
      var added = mutations[i].addedNodes;
      for (var j = 0; j < added.length; j++) {
        var node = added[j];
        if (node.nodeType !== 1) continue;
        if (isEdtImg(node)) { patchEdtImg(node); continue; }
        if (node.classList && node.classList.contains('edt')) {
          node.querySelectorAll(':scope > img').forEach(patchEdtImg);
        }
        if (node.querySelectorAll) patchAllExistingEdt(node);
      }
    }
  });

  if (document.body) {
    observer.observe(document.body, { childList: true, subtree: true });
  } else {
    document.addEventListener('DOMContentLoaded', function () {
      patchAllExistingEdt();
      observer.observe(document.body, { childList: true, subtree: true });
    });
  }
})();

// 2. Video aria-label hoist —
//    Shopify's video_tag Liquid filter auto-renders <video aria-label="..."> when
//    a Video file has alt set (which we did in v2 fix #1 + #2 — hero video and
//    5 portfolio case-study videos). Strict ARIA spec lists <video> as "name from
//    content" not "name from author," so aria-label on <video> is technically
//    non-conformant — and older NVDA / JAWS silently ignore it anyway.
//
//    Fix: move the aria-label content into a visually-hidden sr-only <span>
//    placed immediately before the video's outer container, then remove the
//    aria-label attribute from the <video>. Net effect: modern AT still
//    announces the description (now via the sr-only span in document order);
//    older AT picks it up correctly for the first time; PowerMapper /
//    aria-label-on-prohibited-role rules stop firing.

(function () {
  'use strict';

  function outerContainer(video) {
    // Walk up to the video's wrapping element (Shopify's <video-media>, or fall
    // back to the video itself). The sr-only span goes BEFORE this so it reads
    // before the video in document order regardless of which one focus lands on.
    var p = video.parentElement;
    if (p && p.tagName && p.tagName.toLowerCase() === 'video-media') return p;
    return video;
  }

  function hoistAriaLabel(video) {
    if (!video || video.nodeType !== 1 || video.dataset.adaLabelHoisted) return;
    var label = video.getAttribute('aria-label');
    if (!label) return;
    label = label.trim();
    if (!label) {
      video.removeAttribute('aria-label');
      video.dataset.adaLabelHoisted = '1';
      return;
    }
    var container = outerContainer(video);
    var prev = container.previousElementSibling;
    var alreadyHasSibling = prev && prev.tagName === 'SPAN'
      && prev.classList && prev.classList.contains('sr-only')
      && (prev.textContent || '').trim() === label;
    if (!alreadyHasSibling) {
      var span = document.createElement('span');
      span.className = 'sr-only';
      span.textContent = label;
      span.dataset.adaInjected = '1';
      container.parentNode.insertBefore(span, container);
    }
    video.removeAttribute('aria-label');
    video.dataset.adaLabelHoisted = '1';
  }

  function patchAllExistingVideos(root) {
    (root || document).querySelectorAll('video[aria-label]').forEach(hoistAriaLabel);
  }

  patchAllExistingVideos();

  var videoObserver = new MutationObserver(function (mutations) {
    for (var i = 0; i < mutations.length; i++) {
      var added = mutations[i].addedNodes;
      for (var j = 0; j < added.length; j++) {
        var node = added[j];
        if (node.nodeType !== 1) continue;
        if (node.tagName === 'VIDEO' && node.hasAttribute('aria-label')) hoistAriaLabel(node);
        if (node.querySelectorAll) patchAllExistingVideos(node);
      }
      // Catch the case where aria-label is added to an existing video after mount
      if (mutations[i].type === 'attributes' && mutations[i].attributeName === 'aria-label') {
        var target = mutations[i].target;
        if (target && target.tagName === 'VIDEO') hoistAriaLabel(target);
      }
    }
  });

  if (document.body) {
    videoObserver.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['aria-label'] });
  } else {
    document.addEventListener('DOMContentLoaded', function () {
      patchAllExistingVideos();
      videoObserver.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['aria-label'] });
    });
  }
})();

// 3. Nested <button> inside <a> on partner / agency / developer list pages —
//    PowerMapper flagged six WCAG 2.1 A failures across
//    /pages/shopify-partners-list, /pages/shopify-agencies, and
//    /pages/shopify-developers-list ("The interactive element button must not
//    appear as a descendant of the a element").
//
//    Source: those pages render the location grid as
//      <a href="/pages/shopify-partners/..."><button is="custom-button">…</button></a>
//    The wrapping <a> lives in page-level section-group block data we don't
//    track in git (highlights.liquid + button.liquid produce a single <a> when
//    the URL is set, but the page admin has the URL on the outer <a> and the
//    block's own URL blank, so the button render emits a <button>).
//
//    Fix: at runtime, swap the inner <button is="custom-button"> for a
//    <span class="…"> with the same classes/styles. Visual appearance is
//    unchanged. The outer <a> becomes the only focusable + interactive
//    element. Loading-spinner animation is lost (these are nav links, not
//    submit buttons — the spinner has no purpose here anyway).

(function () {
  'use strict';

  function isButtonInAnchor(btn) {
    return btn && btn.nodeType === 1
      && btn.tagName === 'BUTTON'
      && btn.parentElement
      && btn.parentElement.tagName === 'A';
  }

  function unwrapButton(btn) {
    if (!btn || btn.dataset.adaUnwrapped) return;
    if (!isButtonInAnchor(btn)) return;

    var span = document.createElement('span');
    // Copy every attribute except `type`, `is`, `disabled`, `name`, `form` —
    // those are button-specific and irrelevant on a <span>.
    var skip = { type: 1, is: 1, disabled: 1, name: 1, form: 1 };
    for (var i = 0; i < btn.attributes.length; i++) {
      var attr = btn.attributes[i];
      if (skip[attr.name]) continue;
      span.setAttribute(attr.name, attr.value);
    }
    // Move children over to preserve content + nested elements (the
    // custom-button web component appends a <div> + loader span at
    // attachment time; both are fine to carry forward as visuals).
    while (btn.firstChild) span.appendChild(btn.firstChild);
    span.dataset.adaUnwrapped = '1';
    btn.replaceWith(span);
  }

  function patchAllExistingNestedButtons(root) {
    (root || document).querySelectorAll('a > button').forEach(unwrapButton);
  }

  patchAllExistingNestedButtons();

  var nestedBtnObserver = new MutationObserver(function (mutations) {
    for (var i = 0; i < mutations.length; i++) {
      var added = mutations[i].addedNodes;
      for (var j = 0; j < added.length; j++) {
        var node = added[j];
        if (node.nodeType !== 1) continue;
        if (isButtonInAnchor(node)) { unwrapButton(node); continue; }
        if (node.querySelectorAll) patchAllExistingNestedButtons(node);
      }
    }
  });

  if (document.body) {
    nestedBtnObserver.observe(document.body, { childList: true, subtree: true });
  } else {
    document.addEventListener('DOMContentLoaded', function () {
      patchAllExistingNestedButtons();
      nestedBtnObserver.observe(document.body, { childList: true, subtree: true });
    });
  }
})();
// 4. Video poster <img> alt — Shopify's video_tag Liquid filter emits
//    a poster <img> as a CHILD of the <video> for fallback / preview, but
//    ships it without an alt attribute. PowerMapper flagged three portfolio
//    articles (matter-ht, travoca, gadget-guard) for WCAG 2.1 A 1.1.1.
//
//    These poster images are decorative — the video itself is the content
//    and now carries a hoisted sr-only label (patch #2 above). The poster
//    just shows the first frame before play, so alt="" + aria-hidden is
//    correct.

(function () {
  'use strict';

  function isVideoChildImg(img) {
    return img && img.nodeType === 1 && img.tagName === 'IMG'
      && img.parentElement && img.parentElement.tagName === 'VIDEO';
  }

  function patchPosterImg(img) {
    if (!img || img.dataset.adaPosterPatched) return;
    if (!isVideoChildImg(img)) return;
    img.setAttribute('alt', '');
    img.setAttribute('aria-hidden', 'true');
    img.dataset.adaPosterPatched = '1';
  }

  function patchAllPosterImgs(root) {
    (root || document).querySelectorAll('video > img:not([alt])').forEach(patchPosterImg);
  }

  patchAllPosterImgs();

  var posterObserver = new MutationObserver(function (mutations) {
    for (var i = 0; i < mutations.length; i++) {
      var added = mutations[i].addedNodes;
      for (var j = 0; j < added.length; j++) {
        var node = added[j];
        if (node.nodeType !== 1) continue;
        if (isVideoChildImg(node)) { patchPosterImg(node); continue; }
        if (node.querySelectorAll) patchAllPosterImgs(node);
      }
    }
  });

  if (document.body) {
    posterObserver.observe(document.body, { childList: true, subtree: true });
  } else {
    document.addEventListener('DOMContentLoaded', function () {
      patchAllPosterImgs();
      posterObserver.observe(document.body, { childList: true, subtree: true });
    });
  }
})();
